import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useReducer,
} from "react";
import * as Sentry from "@sentry/react";
import { ChatChannel, ChatMessage, JournalEntry } from "../../../../../fay/Api";
import { PatientLabResponse } from "../../../../../fay/generated";
import {
  AscendingSortedChatMessageList,
  assertSortedChatMessageList,
  mergeChatMessageListsLinearly,
} from "./Utils";
import { useFoodJournal } from "../../../JournalingProvider";
import useLabs from "../../../hooks/useLabs";

// Update the ChatActions type to include currentUserId
export type ChatActions =
  | {
      category: "channel";
      type: "ADD_CHANNELS";
      payload: ChatChannel[];
    }
  | {
      category: "message";
      type: "SEND_MESSAGE";
      toReplaceTempMessageId?: string;
      payload: ChatMessage;
      chatChannelId: string;
    }
  | {
      category: "message";
      type: "REMOVE_TEMP_MESSAGE";
      tempMessageId: string;
      chatChannelId: string;
    }
  | {
      category: "message";
      type: "ADD_PAST_MESSAGES";
      payload: ChatMessage[];
      chatChannelId: string;
    }
  | {
      category: "message";
      type: "ADD_NEW_MESSAGES";
      payload: ChatMessage[];
      chatChannelId: string;
    }
  | {
      category: "message";
      type: "MARK_CHANNEL_AS_READ";
      chatChannelId: string;
      readAt: Date;
      currentUserId: string;
    };

export type ChatState = {
  channels: {
    [chatChannelId: string]:
      | {
          chatChannel: ChatChannel;
          messages: AscendingSortedChatMessageList;
          latestFetchedMessageTimestamp: Date | undefined;
        }
      | undefined;
  };
  // The latest time that we fetched channels from the server.
  // We may update the channels locally, but latestChannelUpdatedAt should
  // only be updated when we fetch the latest channels from the server.
  latestChannelUpdatedAt: Date | undefined;
};

const defaultInitialChannelState = (chatChannel: ChatChannel) => ({
  chatChannel,
  messages: [] as unknown as AscendingSortedChatMessageList,
  latestFetchedMessageTimestamp: undefined,
});

// Shared utility functions
const getChatChannelState = (
  state: ChatState,
  chatChannelId: string,
  actionType: string,
) => {
  const chatChannelState = state.channels[chatChannelId];
  if (!chatChannelState) {
    const message = `Chat channel with ID ${chatChannelId} not found in state; can't run ${actionType}`;
    console.error(message);
    Sentry.captureMessage(message, {
      level: "error",
      extra: {
        channelIdsInState: Object.keys(state.channels),
      },
    });

    return null;
  }
  return chatChannelState;
};

const validateSortedMessages = (messages: ChatMessage[]) => {
  if (!assertSortedChatMessageList(messages)) {
    throw new Error("Messages are not sorted in ascending order of sent_at");
  }
  return messages as AscendingSortedChatMessageList;
};

const updateChannelState = (
  state: ChatState,
  chatChannelId: string,
  updates: Partial<{
    chatChannel: ChatChannel;
    messages: AscendingSortedChatMessageList;
    latestFetchedMessageTimestamp: Date | undefined;
  }>,
): ChatState => {
  const chatChannelState = state.channels[chatChannelId]!;
  return {
    ...state,
    channels: {
      ...state.channels,
      [chatChannelId]: {
        ...chatChannelState,
        ...updates,
      },
    },
  };
};

// Action handlers
const handleMessageAction = <
  T extends Extract<ChatActions, { category: "message" }>,
>(
  state: ChatState,
  action: T,
  handler: (
    channelState: NonNullable<ChatState["channels"][string]>,
  ) => Partial<NonNullable<ChatState["channels"][string]>>,
): ChatState => {
  const chatChannelState = getChatChannelState(
    state,
    action.chatChannelId,
    action.type,
  );
  if (!chatChannelState) {
    console.log("Returning original state", state);
    return state;
  }
  const updates = handler(chatChannelState);
  return updateChannelState(state, action.chatChannelId, updates);
};

// There are some properties of a channel, specifically read_at timestamps, we may not want
// to update from the server. Let's preserve those when the new and old channel latest_message is the same!
const mergeChannelWithUpdates = (
  newChannel: ChatChannel,
  oldChannel?: ChatChannel,
): ChatChannel => {
  if (!oldChannel) {
    return newChannel;
  }

  if (
    oldChannel?.latest_message &&
    newChannel?.latest_message &&
    newChannel.latest_message.message_id ===
      oldChannel.latest_message.message_id &&
    oldChannel?.latest_message?.read_at !== null
  ) {
    const updatedNewChannel = { ...newChannel };
    updatedNewChannel.latest_message = { ...newChannel.latest_message };
    updatedNewChannel.latest_message.read_at =
      oldChannel.latest_message.read_at;
    return updatedNewChannel;
  }

  return newChannel;
};

const handleAddChannels = (
  state: ChatState,
  action: Extract<ChatActions, { type: "ADD_CHANNELS" }>,
  addJournalEntryToCache: (entry: JournalEntry) => void,
  addLabToCache: (lab: PatientLabResponse, patientId: string) => void,
): ChatState => {
  let latestChannelUpdatedAt: Date | undefined = undefined;
  const newChannels = action.payload.reduce(
    (acc, channel) => {
      acc[channel.chat_channel_id] = {
        ...(state.channels[channel.chat_channel_id] ??
          defaultInitialChannelState(channel)),
        chatChannel: mergeChannelWithUpdates(
          channel,
          state.channels[channel.chat_channel_id]?.chatChannel,
        ),
      };

      if (channel.latest_message?.sent_at) {
        const messageDate = channel.latest_message.sent_at;
        if (!latestChannelUpdatedAt || messageDate > latestChannelUpdatedAt) {
          latestChannelUpdatedAt = messageDate;
        }
      }

      if (channel.latest_message?.journal_entry) {
        addJournalEntryToCache(channel.latest_message.journal_entry);
      }

      if (channel.latest_message?.patient_lab) {
        addLabToCache(
          channel.latest_message.patient_lab,
          channel.respondent.id,
        );
      }

      return acc;
    },
    {} as ChatState["channels"],
  );

  return {
    ...state,
    channels: {
      ...state.channels,
      ...newChannels,
    },
    latestChannelUpdatedAt:
      latestChannelUpdatedAt ?? state.latestChannelUpdatedAt,
  };
};

const handleSendMessage = (
  state: ChatState,
  action: Extract<ChatActions, { type: "SEND_MESSAGE" }>,
): ChatState => {
  return handleMessageAction(state, action, (channelState) => {
    const sentMessages = validateSortedMessages([action.payload]);
    return {
      chatChannel: {
        ...channelState.chatChannel,
        latest_message: action.payload,
      },
      messages: mergeChatMessageListsLinearly(
        channelState.messages,
        sentMessages,
      ),
    };
  });
};

const handleAddPastMessages = (
  state: ChatState,
  action: Extract<ChatActions, { type: "ADD_PAST_MESSAGES" }>,
  addJournalEntryToCache: (entry: JournalEntry) => void,
  addLabToCache: (lab: PatientLabResponse, patientId: string) => void,
): ChatState => {
  return handleMessageAction(state, action, (channelState) => {
    const pastMessages = validateSortedMessages(action.payload);
    const earliestPastMessage =
      action.payload.length > 0 ? action.payload[0] : null;

    for (const message of pastMessages) {
      if (message.journal_entry) {
        addJournalEntryToCache(message.journal_entry);
      }
      if (message.patient_lab) {
        addLabToCache(
          message.patient_lab,
          channelState.chatChannel.respondent.id,
        );
      }
    }

    return {
      chatChannel: { ...channelState.chatChannel },
      messages: mergeChatMessageListsLinearly(
        pastMessages,
        channelState.messages,
      ),
      latestFetchedMessageTimestamp:
        channelState.latestFetchedMessageTimestamp ??
        earliestPastMessage?.sent_at,
    };
  });
};

const handleAddNewMessages = (
  state: ChatState,
  action: Extract<ChatActions, { type: "ADD_NEW_MESSAGES" }>,
  addJournalEntryToCache: (entry: JournalEntry) => void,
  addLabToCache: (lab: PatientLabResponse, patientId: string) => void,
): ChatState => {
  return handleMessageAction(state, action, (channelState) => {
    const newMessages = validateSortedMessages(action.payload);
    const latestNewMessage =
      action.payload.length > 0
        ? action.payload[action.payload.length - 1]
        : null;
    const updatedChannel = { ...channelState.chatChannel };

    if (
      latestNewMessage &&
      (!updatedChannel.latest_message ||
        latestNewMessage.sent_at > updatedChannel.latest_message.sent_at)
    ) {
      updatedChannel.latest_message = latestNewMessage;
    }

    for (const message of newMessages) {
      if (message.journal_entry) {
        addJournalEntryToCache(message.journal_entry);
      }
      if (message.patient_lab) {
        addLabToCache(
          message.patient_lab,
          channelState.chatChannel.respondent.id,
        );
      }
    }

    return {
      chatChannel: updatedChannel,
      messages: mergeChatMessageListsLinearly(
        channelState.messages,
        newMessages,
      ),
      latestFetchedMessageTimestamp:
        latestNewMessage?.sent_at ?? channelState.latestFetchedMessageTimestamp,
    };
  });
};

const handleRemoveTempMessage = (
  state: ChatState,
  action: Extract<ChatActions, { type: "REMOVE_TEMP_MESSAGE" }>,
): ChatState => {
  return handleMessageAction(state, action, (channelState) => {
    const index = channelState.messages.findIndex(
      (message) => message.message_id === action.tempMessageId,
    );

    if (index === -1) {
      console.warn(`Message with ID ${action.tempMessageId} not found`);
      return {};
    }

    const messagesWithoutTempMessage = validateSortedMessages([
      ...channelState.messages.slice(0, index),
      ...channelState.messages.slice(index + 1),
    ]);

    return {
      messages: messagesWithoutTempMessage,
    };
  });
};

const handleMarkChannelAsRead = (
  state: ChatState,
  action: Extract<ChatActions, { type: "MARK_CHANNEL_AS_READ" }>,
): ChatState => {
  return handleMessageAction(state, action, (channelState) => {
    const updatedMessages = validateSortedMessages(
      channelState.messages.map((message) =>
        message.read_at === null &&
        message.sent_at <= action.readAt &&
        message.sender_id !== action.currentUserId
          ? { ...message, read_at: action.readAt.toISOString() }
          : message,
      ),
    );

    const latestMessage = channelState.chatChannel.latest_message;
    const updatedChannel = {
      ...channelState.chatChannel,
      latest_message:
        latestMessage &&
        latestMessage.read_at === null &&
        latestMessage.sent_at <= action.readAt &&
        latestMessage.sender_id !== action.currentUserId
          ? { ...latestMessage, read_at: action.readAt.toISOString() }
          : latestMessage,
    } satisfies ChatChannel;

    return {
      chatChannel: updatedChannel,
      messages: updatedMessages,
    };
  });
};

export const chatReducer =
  ({
    addJournalEntryToCache,
    addLabToCache,
  }: {
    addJournalEntryToCache: (entry: JournalEntry) => void;
    addLabToCache: (lab: PatientLabResponse, patientId: string) => void;
  }) =>
  (state: ChatState, action: ChatActions): ChatState => {
    console.debug("chatReducer", action);

    if (action.category === "message") {
      switch (action.type) {
        case "SEND_MESSAGE":
          return handleSendMessage(state, action);
        case "ADD_PAST_MESSAGES":
          return handleAddPastMessages(
            state,
            action,
            addJournalEntryToCache,
            addLabToCache,
          );
        case "ADD_NEW_MESSAGES":
          return handleAddNewMessages(
            state,
            action,
            addJournalEntryToCache,
            addLabToCache,
          );
        case "REMOVE_TEMP_MESSAGE":
          return handleRemoveTempMessage(state, action);
        case "MARK_CHANNEL_AS_READ":
          return handleMarkChannelAsRead(state, action);
        default:
          return state;
      }
    }

    if (action.category === "channel") {
      switch (action.type) {
        case "ADD_CHANNELS":
          return handleAddChannels(
            state,
            action,
            addJournalEntryToCache,
            addLabToCache,
          );
        default:
          return state;
      }
    }

    return state;
  };

const ChatProviderContext = createContext<{
  state: ChatState;
  dispatch: (action: ChatActions) => void;
  stateChannels: ChatChannel[];
  sortedChannels: ChatChannel[];
} | null>(null);

export const useChat = () => {
  const context = useContext(ChatProviderContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const { addJournalEntryToCache } = useFoodJournal();
  const { addLabToCache } = useLabs();

  const [state, dispatch] = useReducer(
    useMemo(
      () =>
        chatReducer({
          addJournalEntryToCache,
          addLabToCache,
        }),
      [addJournalEntryToCache, addLabToCache],
    ),
    {
      channels: {},
      latestChannelUpdatedAt: undefined,
    },
  );

  // Get channels from state and convert to array of ChatChannel objects
  const stateChannels = useMemo(() => {
    return Object.values(state.channels)
      .filter(Boolean)
      .map((channel) => channel!.chatChannel);
  }, [state.channels]);

  // Sort channels by latest_message sent_at time (most recent first)
  const sortedChannels = useMemo(() => {
    return [...stateChannels].sort((a, b) => {
      const aTime = a.latest_message?.sent_at ?? new Date(0);
      const bTime = b.latest_message?.sent_at ?? new Date(0);
      return bTime.getTime() - aTime.getTime(); // Descending order (newest first)
    });
  }, [stateChannels]);

  return (
    <ChatProviderContext.Provider
      value={{ state, stateChannels, sortedChannels, dispatch }}
    >
      {children}
    </ChatProviderContext.Provider>
  );
};
